// Vendor BI header (assessment-viz Surface 2) — replaces the removed composite risk score with
// an honest, analytics-grade multi-assessment header. Every element is fed from real data
// (the /vendors/:id/bi-summary aggregate + the assessments list + RA + cert-status); missing
// values render "—" (never 0); NO composite/blended index; reserved capabilities (Security
// Rating, EASM) are shown INERT — both are non-live and never contribute to a score.

import { Link } from 'react-router-dom';
import { AreaChart, Area, Line, XAxis, YAxis, Tooltip, ReferenceLine, ResponsiveContainer } from 'recharts';
import { Card, CardContent } from '@/components/ui/card';
import { formatDate } from '@/lib/utils';
import { useAssessments } from '@/api/hooks/useAssessments';
import { useVendorBiSummary, useVendorRiskAcceptances, useVendorCertStatus } from '@/api/hooks/useVendors';
import type { Assessment, FindingSeverity } from '@/api/types/assessment.types';
import type { Vendor } from '@/api/types/vendor.types';

const OPEN_STATUSES = ['issued', 'in_progress', 'submitted', 'under_review', 'returned', 'resubmitted', 'reopened'];
const LABEL = 'text-[10.5px] font-bold uppercase tracking-wider text-text-secondary';

const SEVERITY: { key: FindingSeverity; label: string; bar: string; fg: string }[] = [
  { key: 'critical', label: 'Critical', bar: 'bg-severity-critical', fg: 'text-severity-critical' },
  { key: 'high', label: 'High', bar: 'bg-severity-high', fg: 'text-severity-high' },
  { key: 'medium', label: 'Medium', bar: 'bg-severity-medium', fg: 'text-severity-medium' },
  { key: 'low', label: 'Low', bar: 'bg-severity-low', fg: 'text-severity-low' },
  { key: 'informational', label: 'Info', bar: 'bg-severity-info', fg: 'text-severity-info' },
];

function isOverdue(a: Assessment): boolean {
  return !!a.due_date && new Date(a.due_date) < new Date() && !['approved', 'rejected', 'cancelled'].includes(a.status);
}
// Status stripe colours (Design portfolio-row spec, Trap #55 exact-token map):
// overdue #C2453F→cert-expired-stripe · approved #3FB980→cert-valid-stripe ·
// submitted/under-review #8B6DF0→accent-ai · draft #6E86A2→severity-info ·
// active/in-progress #4FA8E8→severity-low. (cancelled/expired→text-faint, not in Design.)
function statusStripe(a: Assessment): string {
  if (isOverdue(a)) return 'bg-cert-expired-stripe';
  if (a.status === 'approved') return 'bg-cert-valid-stripe';
  if (a.status === 'rejected') return 'bg-cert-expired-stripe';
  if (a.status === 'cancelled' || a.status === 'expired') return 'bg-text-faint';
  if (a.status === 'draft') return 'bg-severity-info';
  if (['submitted', 'under_review', 'sent_back', 'returned', 'resubmitted', 'reopened'].includes(a.status)) return 'bg-accent-ai';
  return 'bg-severity-low';
}
// Outcome badge — always returns a badge; a null/unscored outcome shows the neutral "Not scored"
// (Design: never 0%). Trap #55 exact-token map incl. cert-neutral-* for the neutral badge.
function outcomeBadge(a: Assessment): { label: string; cls: string } {
  if (a.outcome === 'pass') return { label: 'Pass', cls: 'bg-cert-valid-bg text-cert-valid-fg border-cert-valid-border' };
  if (a.outcome === 'conditional') return { label: 'Conditional', cls: 'bg-cert-warn-bg text-cert-warn-fg border-cert-warn-border' };
  if (a.outcome === 'fail') return { label: 'Fail', cls: 'bg-cert-expired-bg text-cert-expired-fg border-cert-expired-border' };
  return { label: 'Not scored', cls: 'bg-cert-neutral-bg text-cert-neutral-fg border-cert-neutral-border' };
}
// Sub-line under the assessment name — a status-anchored REAL date (Design row treatment).
// Every field is a real column on the list payload (vnd_assessments.*): approved_at / submitted_at /
// issued_at / due_date. ⚠ The mockup's in-progress "N / M answered" progress counter is NOT in the
// list payload (answered/total live only on the detail/section types), so in-progress falls back to
// the real issued/due dates — never fabricated (honesty north-star).
function assessmentSubline(a: Assessment): { text: string; cls: string } {
  const due = a.due_date ? formatDate(a.due_date) : null;
  if (isOverdue(a)) {
    const days = a.due_date ? Math.max(1, Math.floor((Date.now() - new Date(a.due_date).getTime()) / 86_400_000)) : null;
    return { text: `${days ? `Overdue ${days} day${days === 1 ? '' : 's'}` : 'Overdue'}${due ? ` · due ${due}` : ''}`, cls: 'text-cert-expired-fg' };
  }
  if (a.approved_at) return { text: `Approved ${formatDate(a.approved_at)}`, cls: 'text-text-secondary' };
  if (a.submitted_at) return { text: `Submitted ${formatDate(a.submitted_at)}${due ? ` · due ${due}` : ''}`, cls: 'text-text-secondary' };
  if (a.issued_at) return { text: `Issued ${formatDate(a.issued_at)}${due ? ` · due ${due}` : ''}`, cls: 'text-text-secondary' };
  return { text: 'Not yet issued', cls: 'text-text-secondary' };
}

// The API builds an assessment title as CONCAT(v.name, ' — ', t.name) (assessment.repository.ts findAll),
// so every portfolio label is "{vendor} — {template}" — and the vendor name is already the page H1.
// Strip that EXACT leading "{vendor} — " prefix (space + EM DASH U+2014 + space) to surface the
// differentiating template + version. It's a prefix strip, NOT a split on ' — ' — the template name
// itself can contain em-dashes (e.g. "Veri SAQ Lite — Acme Corp - Copy-01-v6"), which must be preserved.
// Falls through unchanged when the prefix isn't present (differently-shaped title, missing vendor name)
// or would leave an empty string — never blanks the label.
function stripVendorPrefix(label: string, vendorName: string | null | undefined): string {
  if (!label || !vendorName) return label;
  const prefix = `${vendorName} — `;
  if (!label.startsWith(prefix)) return label;
  const stripped = label.slice(prefix.length);
  return stripped.length > 0 ? stripped : label;
}

// Shared column template for the portfolio header + rows so they align (table-like, Design spec):
// [4px stripe] [Assessment — flexes + truncates FIRST] [Status 104px] [Outcome 100px] [Score 54px].
// The numeric/badge tracks are fixed so at narrow widths only the name column shrinks — never re-stacks.
const PORTFOLIO_GRID = 'grid grid-cols-[4px_minmax(0,1fr)_104px_100px_54px] items-center gap-3';

function Kpi({ label, children, context }: { label: string; children: React.ReactNode; context?: React.ReactNode }) {
  return (
    <div className="bg-bg-surface border border-border-subtle rounded-lg p-4 flex flex-col gap-1.5">
      <span className={LABEL}>{label}</span>
      <div className="flex items-baseline gap-2 flex-wrap">{children}</div>
      {context && <span className="text-[11.5px] text-text-secondary">{context}</span>}
    </div>
  );
}

function ReservedPanel({ title, note }: { title: string; note: string }) {
  return (
    <div className="rounded-lg border border-dashed border-border-subtle bg-bg-base/60 p-3.5 flex items-center gap-3">
      <div className="h-9 w-9 shrink-0 rounded-lg border border-dashed border-border-subtle opacity-40" />
      <div className="min-w-0 flex flex-col gap-0.5">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[13px] font-semibold text-text-secondary">{title}</span>
          <span className="text-[9.5px] uppercase tracking-wide text-text-faint border border-border-subtle rounded px-1.5 py-0.5">Not live</span>
        </div>
        <span className="text-[11.5px] text-text-faint leading-snug">{note}</span>
      </div>
    </div>
  );
}

export function VendorBiHeader({ vendor }: { vendor: Vendor }) {
  const vendorId = vendor.id;
  const summary = useVendorBiSummary(vendorId)?.data;
  const list = useAssessments({ vendor_id: vendorId }, 1, 200)?.data;
  const raRows = useVendorRiskAcceptances(vendorId)?.data;
  const certStatus = useVendorCertStatus(vendorId)?.data;

  const assessments = list?.data ?? [];
  const sc = summary?.status_counts ?? {};
  const openCount = OPEN_STATUSES.reduce((n, s) => n + (sc[s] ?? 0), 0);
  const completedCount = sc['approved'] ?? 0;
  const overdue = summary?.overdue ?? 0;

  const trend = (summary?.trend ?? []).map((p) => ({ ...p, scoreNum: Number(p.score) }));
  const latest = trend[trend.length - 1];
  const prior = trend[trend.length - 2];
  const delta = latest && prior ? Math.round(latest.scoreNum - prior.scoreNum) : null;

  const fbs = summary?.findings_by_severity ?? {};
  const totalFindings = SEVERITY.reduce((n, s) => n + (fbs[s.key] ?? 0), 0);
  const maxBand = Math.max(1, ...SEVERITY.map((s) => fbs[s.key] ?? 0));

  const openRa = (raRows ?? []).filter((r) => r.state === 'approved' || r.state === 'pending_approval').length;
  const cert = certStatus?.detail;

  const trendData = trend.map((p) => ({ date: p.date ?? '', score: p.scoreNum, template: p.template ?? '' }));

  return (
    <div className="flex flex-col gap-3.5">
      {/* tier + lifecycle */}
      <div className="flex items-center gap-2 flex-wrap">
        {vendor.tier && <span className="text-[11px] font-bold rounded-md border border-cert-warn-border bg-cert-warn-bg text-cert-warn-fg px-2 py-0.5 capitalize">{String(vendor.tier).replace(/_/g, ' ')}</span>}
        {vendor.status && <span className="text-[11px] font-bold rounded-md border border-border-subtle bg-bg-surface-2 text-text-muted px-2 py-0.5 capitalize">{String(vendor.status).replace(/_/g, ' ')}</span>}
        <span className="text-xs text-text-secondary ml-auto">Assessment overview</span>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-[repeat(auto-fit,minmax(168px,1fr))] gap-3">
        <Kpi label="Latest score" context={latest?.template ? `${latest.template}${latest.date ? ` · ${formatDate(latest.date)}` : ''}` : 'no completed assessment'}>
          <span className="text-2xl font-bold text-text-primary">{latest ? `${Math.round(latest.scoreNum)}%` : '—'}</span>
          {delta != null && <span className={`text-xs font-bold ${delta >= 0 ? 'text-cert-valid-fg' : 'text-cert-expired-fg'}`}>{delta >= 0 ? '▲' : '▼'} {Math.abs(delta)}</span>}
        </Kpi>
        <Kpi label="Assessments" context={overdue > 0 ? `${overdue} overdue` : 'none overdue'}>
          <span className="text-2xl font-bold text-text-primary">{openCount}</span>
          <span className="text-xs text-text-muted">open</span>
          <span className="text-xs text-text-faint">· {completedCount} completed</span>
        </Kpi>
        <Kpi label="Open findings" context={`across ${completedCount + openCount} assessments`}>
          <span className="text-2xl font-bold text-text-primary">{summary ? totalFindings : '—'}</span>
          {(fbs.critical ?? 0) > 0 && <span className="text-xs text-severity-critical font-semibold">{fbs.critical} critical</span>}
        </Kpi>
        <Kpi label="Risk responses" context="open (active + pending)">
          <span className="text-2xl font-bold text-text-primary">{raRows ? openRa : '—'}</span>
        </Kpi>
        <Kpi label="Certifications" context={cert ? `${cert.expired_count} expired · ${cert.withdrawn_count} withdrawn` : undefined}>
          <span className="text-2xl font-bold text-text-primary">{cert ? cert.active_count : '—'}</span>
          <span className="text-xs text-cert-valid-fg">active</span>
        </Kpi>
      </div>

      {/* Trend + portfolio */}
      <div className="grid grid-cols-[repeat(auto-fit,minmax(360px,1fr))] gap-3">
        <Card>
          <CardContent className="p-4 flex flex-col gap-3">
            <div className="flex items-baseline justify-between gap-2 flex-wrap">
              <span className="text-sm font-semibold text-text-primary">Score trend</span>
              <span className="text-[11px] text-text-secondary">completed assessments · pass threshold ~70%</span>
            </div>
            {trendData.length >= 2 ? (
              <ResponsiveContainer width="100%" height={180}>
                <AreaChart data={trendData} margin={{ top: 6, right: 8, bottom: 4, left: 0 }}>
                  <defs>
                    <linearGradient id="vbiTrend" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#4FA8E8" stopOpacity={0.32} />
                      <stop offset="100%" stopColor="#4FA8E8" stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="date" tickFormatter={(t) => { try { return formatDate(t); } catch { return t; } }} tick={{ fill: '#8DA0B8', fontSize: 10 }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
                  <YAxis hide domain={[0, 100]} />
                  <ReferenceLine y={70} stroke="#6E86A2" strokeDasharray="4 4" />
                  <Tooltip
                    contentStyle={{ background: '#111E30', border: '1px solid #1E2E42', borderRadius: 8, fontSize: 12 }}
                    labelFormatter={(l) => { try { return formatDate(String(l)); } catch { return String(l); } }}
                    formatter={(v: number, _n, p: { payload?: { template?: string } }) => [`${Math.round(v)}%`, p?.payload?.template || 'score']}
                  />
                  <Area type="monotone" dataKey="score" stroke="#4FA8E8" strokeWidth={2.2} fill="url(#vbiTrend)" dot={{ r: 3, fill: '#0A1320', stroke: '#4FA8E8', strokeWidth: 2 }} />
                  <Line type="monotone" dataKey="score" stroke="#4FA8E8" strokeWidth={0} dot={false} />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[180px] flex items-center justify-center text-center px-4">
                <span className="text-xs text-text-secondary leading-relaxed">
                  {trend.length === 1
                    ? `One completed assessment (${Math.round(trend[0].scoreNum)}%). A trend line appears after the next completion.`
                    : 'Trend needs at least two completed assessments.'}
                </span>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Portfolio — fixed-height tile: title + column-header sit OUTSIDE the scroll body; the body
            shows exactly 4 × 52px rows (208px) then scrolls, so tile height is stable at any count. */}
        <Card className="flex flex-col overflow-hidden">
          <CardContent className="p-0 flex flex-col min-h-0">
            <div className="shrink-0 flex items-baseline justify-between gap-3 px-[18px] pt-[15px] pb-3">
              <span className="text-sm font-semibold text-text-primary">Assessment portfolio</span>
            </div>
            {assessments.length === 0 ? (
              <div className="h-[208px] border-t border-border-subtle flex items-center justify-center px-6 text-center">
                <span className="text-xs text-text-secondary leading-relaxed">No assessments issued to this vendor yet.</span>
              </div>
            ) : (
              <>
                {/* Column header — plinth, structurally fixed outside the scroll body (no sticky needed) */}
                <div className={`${PORTFOLIO_GRID} shrink-0 bg-bg-base border-y border-border-subtle pl-0 pr-[18px] py-[7px] text-[10px] font-bold uppercase tracking-[0.13em] text-text-secondary`}>
                  <span />
                  <span className="pl-2.5">Assessment</span>
                  <span>Status</span>
                  <span>Outcome</span>
                  <span className="justify-self-end">Score</span>
                </div>
                {/* Scroll body — exactly 4 rows visible (4 × 52px = 208px), scrolls beyond */}
                <div className="h-[208px] min-h-0 overflow-y-auto overscroll-contain" style={{ scrollbarGutter: 'stable' }}>
                  {assessments.map((a, i) => {
                    const badge = outcomeBadge(a);
                    const overdueRow = isOverdue(a);
                    const label = stripVendorPrefix(a.template_name ?? a.title, vendor.name);
                    const statusText = overdueRow ? 'Overdue' : String(a.status).replace(/_/g, ' ');
                    const sub = assessmentSubline(a);
                    const last = i === assessments.length - 1;
                    return (
                      <Link
                        key={a.id}
                        to={`/assessments/${a.id}`}
                        className={`${PORTFOLIO_GRID} h-[52px] box-border pl-0 pr-[18px] overflow-hidden ${i % 2 === 1 ? 'bg-white/[0.02]' : ''} ${last ? '' : 'border-b border-border-subtle'} hover:bg-white/[0.04] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-cyan/55 focus-visible:ring-inset transition-colors`}
                      >
                        {/* Status stripe — full-height left edge (preserved), status-coloured */}
                        <span className={`w-1 self-stretch ${statusStripe(a)}`} />
                        {/* Assessment — name (truncate + tooltip) over a status-anchored real date */}
                        <span className="min-w-0 flex flex-col gap-0.5 pl-2.5 overflow-hidden">
                          <span className="truncate text-[13.5px] font-medium text-text-primary" title={label}>{label}</span>
                          <span className={`truncate text-[11.5px] ${sub.cls}`}>{sub.text}</span>
                        </span>
                        {/* Status — its own column */}
                        <span className={`min-w-0 truncate text-xs capitalize ${overdueRow ? 'text-cert-expired-fg' : 'text-text-muted'}`}>{statusText}</span>
                        {/* Outcome — pass / conditional / fail / not-scored badge */}
                        <span className={`justify-self-start min-w-[84px] text-center text-[10.5px] font-bold rounded-md border px-2 py-0.5 ${badge.cls}`}>{badge.label}</span>
                        {/* Score — right-aligned, tabular mono */}
                        <span className="justify-self-end text-right text-[13px] font-mono tabular-nums text-text-primary">{a.final_score != null ? `${Math.round(Number(a.final_score))}%` : <span className="text-text-faint">—</span>}</span>
                      </Link>
                    );
                  })}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Findings by severity + reserved panels */}
      <div className="grid grid-cols-[repeat(auto-fit,minmax(300px,1fr))] gap-3">
        <Card>
          <CardContent className="p-4 flex flex-col gap-3">
            <div className="flex items-baseline justify-between"><span className="text-sm font-semibold text-text-primary">Findings by severity</span><span className="text-[11px] text-text-secondary">all assessments</span></div>
            {totalFindings === 0 ? (
              <p className="text-xs text-text-secondary">No flagged findings across this vendor's assessments.</p>
            ) : (
              <div className="flex flex-col gap-2">
                {SEVERITY.map((s) => (
                  <div key={s.key} className="flex items-center gap-2.5">
                    <span className="flex-[0_0_72px] text-xs text-text-primary">{s.label}</span>
                    <span className="flex-1 h-4 rounded bg-cert-neutral-bg overflow-hidden">
                      <span className={`block h-full rounded ${s.bar}`} style={{ width: `${((fbs[s.key] ?? 0) / maxBand) * 100}%` }} />
                    </span>
                    <span className={`flex-[0_0_28px] text-xs font-mono text-right ${s.fg}`}>{fbs[s.key] ?? 0}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <div className="flex flex-col gap-3">
          <ReservedPanel title="Security Rating" note="Data source not live — this panel is reserved and contributes to no score." />
          <ReservedPanel title="EASM exposure" note="Module not yet available. Space is held so the layout won't shift when it ships." />
        </div>
      </div>
    </div>
  );
}
